//
// # Expressions (`Expr`)
//

// NPM Imports
import typia from "typia";
// Local Imports
import { exhaust, Validator } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { Text, type TextFormat } from "../graphics/text";
import { Ident } from "../ident";
import { Eval } from "./evals";

// Schema re-exports
export type ExprKind = schema.symbol.ExprKind;
export const ExprKind = schema.symbol.ExprKind;

// Shorthand for "grab the `Expr` varient with kind==KindT".
type X<KindT extends ExprKind> = Extract<Expr, { kind: KindT }>;
export type ExprVariant<T extends ExprKind> = X<T>;

export type Expr = schema.symbol.Expr;
export const Expr = {
  //
  InstParam: {
    // Create a new `InstParam` expression.
    new: (arg: {
      instName: string | undefined;
      spec: InstParamSpec;
      fmt: TextFormat;
    }): X<typeof ExprKind.InstParam> => {
      const { fmt, spec, instName } = arg;
      return {
        ...fmt,
        // instName,
        kind: ExprKind.InstParam,
        spec,
      };
    },
  },

  InstName: {
    new: (fmt: TextFormat): X<typeof ExprKind.InstName> => {
      return { ...fmt, kind: ExprKind.InstName };
    },
    evaluate: (expr: X<typeof ExprKind.InstName>, name: string): Eval => {
      return Eval.ok(expr, name);
    },
  },

  CellName: {
    evaluate: (
      expr: X<typeof ExprKind.CellName>,
      ctx: { target: Ident }
    ): Eval => {
      const { target } = ctx;
      // For PDK generator targets like ["generators", "gf12", "rmres"],
      // display just the device name (last segment) instead of the full path.
      if (
        target.kind === "hierpath" &&
        target.path.length === 3 &&
        target.path[0] === "generators"
      ) {
        return Eval.ok(expr, target.path[2]);
      }
      return Eval.ok(expr, Ident.displayName(ctx.target));
    },
  },

  PortDebug: {
    new: (fmt: TextFormat, name: string): X<typeof ExprKind.PortDebug> => {
      return { ...fmt, name, kind: ExprKind.PortDebug };
    },
    evaluate: (expr: X<typeof ExprKind.PortDebug>, _ctx?: any): Eval => {
      return Eval.doNotDisplay(expr); // FIXME! sometimes display!
    },
  },

  PortName: {
    new: (name: string, fmt: TextFormat): X<typeof ExprKind.PortName> => {
      return { ...fmt, name, kind: ExprKind.PortName };
    },
    evaluate: (expr: X<typeof ExprKind.PortName>): Eval => {
      return Eval.ok(expr, expr.name);
    },
  },

  Invalid: {
    new: (fmt: TextFormat, text: string): X<typeof ExprKind.Invalid> => {
      return { ...fmt, text, kind: ExprKind.Invalid };
    },
    evaluate: (expr: X<typeof ExprKind.Invalid>, _ctx?: any): Eval => {
      return Eval.error(expr, expr.text);
    },
  },

  // Get the string content to use when displaying an (unevaluated) `Expr`.
  textString: (e: Expr): string | null => {
    const { kind } = e;
    switch (kind) {
      // The good kinds: names of things
      case ExprKind.PortName:
        return "{PortName}";
      case ExprKind.InstName:
        return "{InstName}";
      case ExprKind.CellName:
        return "{CellName}";
      case ExprKind.PortDebug:
        return `{Port:${e.name}}`;
      case ExprKind.InstParam:
        switch (e.spec.kind) {
          case "name":
            return `{Param:${e.spec.name}}`;
          case "index":
            return `{Param:${e.spec.index}}`;
          default:
            exhaust(e.spec);
        }

      // Weird, error, or unsupported cases
      case ExprKind.Invalid:
        return null;

      default: // Exhaustiveness check
        exhaust(kind);
    }
  },

  // Get a `Text` graphics-element to use to display an `Expr`.
  // This is generally used when the expression does not have an evaluated result, e.g. in symbol-editing contexts,.
  textElem: (e: Expr): Text | null => {
    const text = Expr.textString(e);
    if (!text) return null;
    return Text.new({ ...e, text });
  },

  // JSON Validation & Writing
  validator: new Validator(typia.createValidate<Expr>()),
  stringify: typia.json.createStringify<Expr>(),
};

export type InstParamSpec = schema.symbol.InstParamSpec;
